import { TAppError, IBaseAction, IBaseState } from '@/store/types/base.state';
import { ISearchFilters } from '@/store/models/search.model';
import { IUser } from '@/features/user/store/user.model';
import { IWallet } from '@/features/wallets/store/models/wallet.model';
import { IPaymentCard, ICardTransaction } from '@/features/cards/store/models/card.model';

export interface ICursor {
  current?: string;
  prev?: string | null;
  next?: string | null;
}
export interface IInfiniteCursor {
  current?: string | null;
  firstCursor?: string | null;
  next?: string | null;
}

export interface IPagination {
  totalPages: number;
  currentPage: number;
}

export interface IPaginateElements<T> {
  elements: T[];
  pagination: IPagination;
}

export interface ICursorElements<T> {
  elements: T[];
  cursor: ICursor;
}

export interface IState<T = void> {
  state?: T;
  isLoading?: boolean;
  error?: TAppError;
}

export enum SearchActionTypes {
  SEARCH = 'SEARCH.SEARCH',
  SEARCH_SUCCESS = 'SEARCH.SEARCH_SUCCESS',
  SEARCH_FAIL = 'SEARCH.SEARCH_FAIL',

  SEARCH_USERS = 'SEARCH.SEARCH_USERS',
  SEARCH_USERS_SUCCESS = 'SEARCH.SEARCH_USERS_SUCCESS',
  SEARCH_USERS_FAIL = 'SEARCH.SEARCH_USERS_FAIL',

  SEARCH_WALLETS = 'SEARCH.SEARCH_WALLETS',
  SEARCH_WALLETS_SUCCESS = 'SEARCH.SEARCH_WALLETS_SUCCESS',
  SEARCH_WALLETS_FAIL = 'SEARCH.SEARCH_WALLETS_FAIL',

  SEARCH_CARDS = 'SEARCH.SEARCH_CARDS',
  SEARCH_CARDS_SUCCESS = 'SEARCH.SEARCH_CARDS_SUCCESS',
  SEARCH_CARDS_FAIL = 'SEARCH.SEARCH_CARDS_FAIL',
}

export interface ISearchAction extends IBaseAction {
  type: typeof SearchActionTypes.SEARCH;
}

export interface ISearchSuccessAction extends IBaseAction {
  type: typeof SearchActionTypes.SEARCH_SUCCESS;
  paginateUsers?: ICursorElements<IUser>;
  paginateWallets?: ICursorElements<IWallet>;
  paginateCards?: ICursorElements<IPaymentCard>;
  paginateTransactions?: ICursorElements<ICardTransaction>;
  currentFilters: ISearchFilters;
}

export interface ISearchFailAction extends IBaseAction {
  type: typeof SearchActionTypes.SEARCH_FAIL;
  error: TAppError;
}

export interface ISearchUsersAction extends IBaseAction {
  type: typeof SearchActionTypes.SEARCH_USERS;
}

export interface ISearchUsersSuccessAction extends IBaseAction {
  type: typeof SearchActionTypes.SEARCH_USERS_SUCCESS;
  paginateUsers?: ICursorElements<IUser>;
}

export interface ISearchUsersFailAction extends IBaseAction {
  type: typeof SearchActionTypes.SEARCH_USERS_FAIL;
  error: TAppError;
}

export interface ISearchWalletsAction extends IBaseAction {
  type: typeof SearchActionTypes.SEARCH_WALLETS;
}

export interface ISearchWalletsSuccessAction extends IBaseAction {
  type: typeof SearchActionTypes.SEARCH_WALLETS_SUCCESS;
  paginateWallets?: ICursorElements<IWallet>;
}

export interface ISearchWalletsFailAction extends IBaseAction {
  type: typeof SearchActionTypes.SEARCH_WALLETS_FAIL;
  error: TAppError;
}

export interface ISearchCardsAction extends IBaseAction {
  type: typeof SearchActionTypes.SEARCH_CARDS;
}

export interface ISearchCardsSuccessAction extends IBaseAction {
  type: typeof SearchActionTypes.SEARCH_CARDS_SUCCESS;
  paginateCards?: ICursorElements<IPaymentCard>;
}

export interface ISearchCardsFailAction extends IBaseAction {
  type: typeof SearchActionTypes.SEARCH_CARDS_FAIL;
  error: TAppError;
}

export type TSearchAction =
  | ISearchAction
  | ISearchSuccessAction
  | ISearchFailAction
  | ISearchUsersAction
  | ISearchUsersSuccessAction
  | ISearchUsersFailAction
  | ISearchWalletsAction
  | ISearchWalletsSuccessAction
  | ISearchWalletsFailAction
  | ISearchCardsAction
  | ISearchCardsSuccessAction
  | ISearchCardsFailAction;

export interface ISearchState extends IBaseState {
  paginateUsers?: ICursorElements<IUser>;
  paginateWallets?: ICursorElements<IWallet>;
  paginateCards?: ICursorElements<IPaymentCard>;
  paginateTransactions?: ICursorElements<ICardTransaction>;
  currentFilters?: ISearchFilters;
}
